import { CheckOutlined } from '@ant-design/icons';
import { Button, ButtonProps } from '@mm/components';
import { OAuthPopupActionButton } from '@mm/components/src/OAuthPopupActionButton/OAuthPopupActionButton';
import { landmineNames } from '@mm/constants/landmine-names';
import { SalesForceIcon } from '@mm/icons';
import { StoreState } from '@mm/state/store-interfaces';
import { Spin } from 'antd';
import React, { ReactNode, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { ButtonColor } from '../../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { BaseIntegration } from '../../../BaseIntegration/BaseIntegration';
import { BaseIntegrationHeader } from '../../../BaseIntegration/BaseIntegrationHeader';
import { SalesForceSettings } from './SalesForceSettings';
import { useConnectSalesForce } from './use-connect-salesforce';
import styles from './SalesForceIntegrations.module.css';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { useSalesForceCRUDActions } from '@mm/state/actions/SalesForceCRUDActions';

export const SalesForceIntegrations = () => {
  const accounts = useSelector(
    (state: StoreState) => state.salesForce.accounts,
  );
  const hasNativeIntegrationsFeature = useSelector(
    combinedFeatureFlagsSelector,
  ).nativeIntegrations;
  const connectedAccount = accounts?.[0];
  const oAuthButtonName = 'Add a SalesForce Account';
  const otherProps = {
    landmineName: landmineNames.SALESFORCE_UPGRADE,
    featureFlag: 'nativeIntegrations',
  };
  const { invalidateStore } = useSalesForceCRUDActions();
  const {
    retrieveSalesForceAccounts,
    busy,
    fetchOAuthUrl,
    salesForceConnection,
    getSaleForceConnections,
  } = useConnectSalesForce();

  const connectDisabled = !!accounts.length || connectedAccount?.readOnly;

  useEffect(() => {
    retrieveSalesForceAccounts();
    getSaleForceConnections();
  }, [accounts.length]);

  useEffect(
    () => () => {
      invalidateStore();
    },
    [],
  );

  const oAuthButton = (
    buttonName?: string | ReactNode,
    buttonProps?: Pick<ButtonProps, 'size' | 'color' | 'css' | 'disabled'>,
  ) => {
    const defaultButtonProps = {
      css: {
        container: 'integrations__header-add-button-secondary',
      },
    };

    return (
      <OAuthPopupActionButton
        fetchOauthPopupUrl={fetchOAuthUrl}
        oauthSuccessMessageType="salesForceAccountConnected"
        buttonProps={buttonProps || defaultButtonProps}
        windowProps={{ target: '_self' }}
        spinnerProps={{ width: '30px' }}
      >
        {buttonName || 'Add an Account'}
      </OAuthPopupActionButton>
    );
  };

  const header = connectDisabled ? (
    <Button
      css={{ container: 'integrations__header-add-button-secondary' }}
      color={ButtonColor.secondaryBlue}
      disabled={connectDisabled}
    >
      <CheckOutlined /> Connected Directly
    </Button>
  ) : (
    oAuthButton()
  );

  return (
    <Spin spinning={busy}>
      <BaseIntegration
        titleComponent={
          <BaseIntegrationHeader
            title="SalesForce"
            upgradeButtonName={
              !connectDisabled ? 'Add an Account' : 'Add a Connection'
            }
            subtitle="Automatically sync your Customers.ai contacts to SalesForce"
            icon={<SalesForceIcon />}
            headerRightComponent={header}
            css={{ icon: 'integrations__salesforce-header-icon ' }}
            {...otherProps}
          />
        }
        integrationType="salesforce"
        emptyStateConfig={{
          button: hasNativeIntegrationsFeature
            ? oAuthButton(oAuthButtonName, {
                css: { container: styles.emptyStateButton },
              })
            : oAuthButtonName,
          icon: <SalesForceIcon size={60} />,
          ...otherProps,
        }}
        showEmptyState={!accounts.length && !salesForceConnection}
      >
        {salesForceConnection && (
          <SalesForceSettings
            connection={salesForceConnection}
            account={connectedAccount}
            oAuthButton={oAuthButton}
          />
        )}
      </BaseIntegration>
    </Spin>
  );
};
